// ===================================
// ENHANCED ACTION FORM WITH AUTO-POPULATION - src/components/ActionForm.tsx
// ===================================
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import ActionsApi from '../services/actionsApi';
import { 
  Action, 
  Topic, 
  ActionField, 
  CreateActionRequest, 
  UpdateActionRequest,
  ActionFormData,
  ActionFormErrors
} from '../types';
import { 
  X, 
  Plus, 
  Trash2, 
  CheckCircle, 
  XCircle, 
  Clock,
  RefreshCw,
  Download,
  Code,
  Info,
  AlertTriangle,
  Wand2,
  Eye,
  EyeOff
} from 'lucide-react';
import Button from './ui/button';
import Badge from './ui/badge';

interface ActionFormProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  topic: Topic;
  action?: Action | null;
  onClose: () => void;
  onSuccess: (action: Action) => void;
}

const ActionForm: React.FC<ActionFormProps> = ({
  isOpen,
  mode,
  topic,
  action,
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  // Form state
  const [formData, setFormData] = useState<ActionFormData & { auto_populate_from_flow: boolean }>({
    action_name: '',
    description: '',
    flow_name: '',
    input_params: [],
    output_params: [],
    required_fields: [],
    example_use_case: '',
    sample_data: '{}',
    auto_populate_from_flow: true
  });

  const [errors, setErrors] = useState<ActionFormErrors>({});
  const [loading, setLoading] = useState(false);
  const [showCreateAnother, setShowCreateAnother] = useState(false);

  // Flow validation and auto-population state
  const [flowValidation, setFlowValidation] = useState({
    status: 'idle' as 'idle' | 'validating' | 'valid' | 'invalid',
    exists: false,
    definition: null as any,
    error: null as string | null,
    lastChecked: null as string | null
  });

  // Auto-populated parameters (separate from manual params)
  const [autoParams, setAutoParams] = useState({
    input_params: [] as ActionField[],
    output_params: [] as ActionField[],
    required_fields: [] as string[],
    sample_data: null as any
  });

  // Parameter preview state
  const [showParamPreview, setShowParamPreview] = useState(false);

  // Initialize form data
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && action) {
        const normalized = ActionsApi.normalizeAction(action);
        setFormData({
          action_name: normalized.action_name,
          description: normalized.description || '',
          flow_name: normalized.flow_name,
          input_params: normalized.input_params || [],
          output_params: normalized.output_params || [],
          required_fields: normalized.required_fields || [],
          example_use_case: normalized.example_use_case || '',
          sample_data: normalized.sample_data ? JSON.stringify(normalized.sample_data, null, 2) : '{}',
          auto_populate_from_flow: false // Disable auto-populate for edit mode
        });
      } else {
        // Reset for create mode
        setFormData({
          action_name: '',
          description: '',
          flow_name: '',
          input_params: [],
          output_params: [],
          required_fields: [],
          example_use_case: '',
          sample_data: '{}',
          auto_populate_from_flow: true
        });
      }
      setErrors({});
      setFlowValidation({
        status: 'idle',
        exists: false,
        definition: null,
        error: null,
        lastChecked: null
      });
      setAutoParams({
        input_params: [],
        output_params: [],
        required_fields: [],
        sample_data: null
      });
      setShowCreateAnother(false);
      setShowParamPreview(false);
    }
  }, [isOpen, mode, action]);

  // Validate flow when flow_name changes and auto-populate is enabled
  useEffect(() => {
    if (formData.flow_name.trim() && formData.auto_populate_from_flow && mode === 'create') {
      const timeoutId = setTimeout(() => {
        validateAndPopulateFromFlow(formData.flow_name.trim());
      }, 500); // Debounce API calls

      return () => clearTimeout(timeoutId);
    }
  }, [formData.flow_name, formData.auto_populate_from_flow, mode]);

  // Validate flow and auto-populate parameters
  const validateAndPopulateFromFlow = async (flowName: string) => {
    if (!flowName.trim()) return;

    setFlowValidation(prev => ({ ...prev, status: 'validating', error: null }));

    try {
      const result = await ActionsApi.validateFlow({ flow_name: flowName });
      
      if (result.exists && (result as any).definition) {
        setFlowValidation({
          status: 'valid',
          exists: true,
          definition: (result as any).definition,
          error: null,
          lastChecked: new Date().toISOString()
        });

        // Auto-populate parameters from flow definition
        const flowDef = (result as any).definition;
        const inputParams = flowDef.inputs?.map((input: any) => ({
          name: input.name,
          type: mapSalesforceTypeToActionType(input.type || input.dataType),
          required: false, // Always start as optional - user will decide what's required
          sample_value: generateSampleValue(input.name, input.type || input.dataType)
        })) || [];

        const outputParams = flowDef.outputs?.map((output: any) => ({
          name: output.name,
          type: mapSalesforceTypeToActionType(output.type || output.dataType)
        })) || [];

        // Don't auto-populate required fields - let user decide based on business needs
        const requiredFields: string[] = [];

        // Generate sample data
        const sampleData = inputParams.reduce((acc: any, param: ActionField) => {
          if (param.sample_value) {
            acc[param.name] = param.sample_value;
          }
          return acc;
        }, {});

        setAutoParams({
          input_params: inputParams,
          output_params: outputParams,
          required_fields: requiredFields,
          sample_data: sampleData
        });

        // Update form data with auto-populated values
        setFormData(prev => ({
          ...prev,
          input_params: inputParams,
          output_params: outputParams,
          required_fields: requiredFields,
          sample_data: Object.keys(sampleData).length > 0 ? JSON.stringify(sampleData, null, 2) : '{}'
        }));

        addNotification({
          type: 'success',
          title: 'Flow Validated',
          message: `Flow "${flowName}" found with ${inputParams.length} inputs and ${outputParams.length} outputs. Parameters auto-populated. Please review and mark required fields manually.`
        });

      } else {
        setFlowValidation({
          status: 'invalid',
          exists: false,
          definition: null,
          error: result.error || `Flow "${flowName}" not found`,
          lastChecked: new Date().toISOString()
        });

        addNotification({
          type: 'error',
          title: 'Flow Not Found',
          message: `Salesforce flow "${flowName}" does not exist or is not accessible.`
        });
      }
    } catch (error: any) {
      setFlowValidation({
        status: 'invalid',
        exists: false,
        definition: null,
        error: error.message,
        lastChecked: new Date().toISOString()
      });

      addNotification({
        type: 'error',
        title: 'Flow Validation Failed',
        message: error.message
      });
    }
  };

  // Helper function to map Salesforce types to action types
  const mapSalesforceTypeToActionType = (salesforceType: string): string => {
    if (!salesforceType) return 'string';
    
    const type = salesforceType.toLowerCase();
    const typeMapping: { [key: string]: string } = {
      'string': 'string',
      'text': 'string',
      'textarea': 'string',
      'email': 'string',
      'phone': 'string',
      'url': 'string',
      'number': 'number',
      'decimal': 'number',
      'integer': 'number',
      'double': 'number',
      'currency': 'number',
      'percent': 'number',
      'boolean': 'boolean',
      'checkbox': 'boolean',
      'date': 'string',
      'datetime': 'string',
      'time': 'string',
      'picklist': 'picklist',
      'multipicklist': 'multiselect',
      'combobox': 'string',
      'reference': 'string',
      'id': 'string'
    };
    
    return typeMapping[type] || 'string';
  };

  // Helper function to generate sample values
  const generateSampleValue = (fieldName: string, fieldType: string): string => {
    const name = fieldName.toLowerCase();
    const type = (fieldType || '').toLowerCase();
    
    // Name-based samples
    if (name.includes('email')) return 'john.doe@example.com';
    if (name.includes('phone')) return '555-123-4567';
    if (name.includes('name') && !name.includes('file')) return 'John Doe';
    if (name.includes('subject')) return 'Sample Subject';
    if (name.includes('description')) return 'Sample description text';
    if (name.includes('status')) return 'New';
    if (name.includes('priority')) return 'Medium';
    if (name.includes('origin')) return 'Phone';
    if (name.includes('business') && name.includes('id')) return '12345';
    if (name.includes('case') && name.includes('number')) return 'CS-001234';
    if (name.includes('amount')) return '1000.00';
    if (name.includes('date')) return '2024-01-15';
    if (name.includes('url') || name.includes('website')) return 'https://example.com';
    
    // Type-based samples
    switch (type) {
      case 'email': return 'user@example.com';
      case 'phone': return '555-123-4567';
      case 'number':
      case 'decimal':
      case 'integer': return '100';
      case 'boolean':
      case 'checkbox': return 'true';
      case 'date': return '2024-01-15';
      case 'datetime': return '2024-01-15T10:30:00Z';
      case 'url': return 'https://example.com';
      case 'picklist': return 'Option 1';
      default: return 'Sample value';
    }
  };

  // Manual parameter management (when auto-populate is disabled)
  const cleanFieldConstraints = (field: ActionField): ActionField => {
    const cleaned = { ...field };
    
    if (cleaned.constraints) {
      const hasValidConstraints = 
        (cleaned.constraints.min !== undefined && cleaned.constraints.min !== null) ||
        (cleaned.constraints.max !== undefined && cleaned.constraints.max !== null) ||
        (cleaned.constraints.values && cleaned.constraints.values.length > 0);
      
      if (!hasValidConstraints) {
        delete cleaned.constraints;
      } else {
        if (cleaned.constraints.values && cleaned.constraints.values.length === 0) {
          delete cleaned.constraints.values;
        }
      }
    }
    
    return cleaned;
  };

  const addInputField = () => {
    if (formData.auto_populate_from_flow) {
      addNotification({
        type: 'info',
        title: 'Auto-populate Enabled',
        message: 'Disable auto-populate to manually add input fields.'
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      input_params: [...prev.input_params, {
        name: '',
        type: 'string',
        required: false,
        sample_value: ''
      }]
    }));
  };

  const removeInputField = (index: number) => {
    if (formData.auto_populate_from_flow) return;

    setFormData(prev => ({
      ...prev,
      input_params: prev.input_params.filter((_, i) => i !== index),
      required_fields: prev.required_fields.filter(name => 
        name !== prev.input_params[index]?.name
      )
    }));
  };

  const updateInputField = (index: number, field: Partial<ActionField>) => {
    if (formData.auto_populate_from_flow) return;

    setFormData(prev => {
      const newInputParams = [...prev.input_params];
      const oldName = newInputParams[index]?.name;
      const newField = { ...newInputParams[index], ...field };
      
      newInputParams[index] = cleanFieldConstraints(newField);
      
      let newRequiredFields = [...prev.required_fields];
      if (oldName && field.name && oldName !== field.name) {
        newRequiredFields = newRequiredFields.map(name => 
          name === oldName ? field.name as string : name
        );
      }
      
      return {
        ...prev,
        input_params: newInputParams,
        required_fields: newRequiredFields
      };
    });
  };

  const addOutputField = () => {
    if (formData.auto_populate_from_flow) {
      addNotification({
        type: 'info',
        title: 'Auto-populate Enabled',
        message: 'Disable auto-populate to manually add output fields.'
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      output_params: [...prev.output_params, {
        name: '',
        type: 'string'
      }]
    }));
  };

  const removeOutputField = (index: number) => {
    if (formData.auto_populate_from_flow) return;

    setFormData(prev => ({
      ...prev,
      output_params: prev.output_params.filter((_, i) => i !== index)
    }));
  };

  const updateOutputField = (index: number, field: Partial<ActionField>) => {
    if (formData.auto_populate_from_flow) return;

    setFormData(prev => {
      const newOutputParams = [...prev.output_params];
      newOutputParams[index] = cleanFieldConstraints({ ...newOutputParams[index], ...field });
      return {
        ...prev,
        output_params: newOutputParams
      };
    });
  };

  const toggleRequiredField = (fieldName: string) => {
    setFormData(prev => {
      const isCurrentlyRequired = prev.required_fields.includes(fieldName);
      const newRequiredFields = isCurrentlyRequired
        ? prev.required_fields.filter(name => name !== fieldName)
        : [...prev.required_fields, fieldName];
      
      return {
        ...prev,
        required_fields: newRequiredFields
      };
    });
  };

  // Validate form with detailed errors
  const validateForm = (): boolean => {
    const newErrors: ActionFormErrors = {};

    if (!formData.action_name.trim()) {
      newErrors.action_name = 'Action name is required';
    } else if (formData.action_name.length > 255) {
      newErrors.action_name = 'Action name must be less than 255 characters';
    }

    if (!formData.flow_name.trim()) {
      newErrors.flow_name = 'Flow name is required';
    } else if (formData.flow_name.length > 255) {
      newErrors.flow_name = 'Flow name must be less than 255 characters';
    }

    // For auto-populate mode, validate that flow is valid
    if (formData.auto_populate_from_flow && mode === 'create' && flowValidation.status !== 'valid') {
      newErrors.flow_name = 'Please ensure the flow exists and is valid before creating the action';
    }

    // Validate input fields (if not auto-populated or in edit mode)
    if (!formData.auto_populate_from_flow || mode === 'edit') {
      const inputFieldNames = formData.input_params
        .map(field => field.name.trim())
        .filter(Boolean);
      
      const uniqueInputNames = new Set(inputFieldNames);
      if (inputFieldNames.length !== uniqueInputNames.size) {
        newErrors.input_params = 'Input field names must be unique';
      }

      const hasEmptyInputNames = formData.input_params.some(field => !field.name.trim());
      if (hasEmptyInputNames) {
        newErrors.input_params = 'All input fields must have names';
      }

      // Validate output fields
      const outputFieldNames = formData.output_params
        .map(field => field.name.trim())
        .filter(Boolean);
      
      const uniqueOutputNames = new Set(outputFieldNames);
      if (outputFieldNames.length !== uniqueOutputNames.size) {
        newErrors.output_params = 'Output field names must be unique';
      }

      const hasEmptyOutputNames = formData.output_params.some(field => !field.name.trim());
      if (hasEmptyOutputNames) {
        newErrors.output_params = 'All output fields must have names';
      }

      // Validate required fields
      const invalidRequiredFields = formData.required_fields.filter(
        name => !inputFieldNames.includes(name)
      );
      if (invalidRequiredFields.length > 0) {
        newErrors.required_fields = `Required fields must exist in inputs: ${invalidRequiredFields.join(', ')}`;
      }
    }

    // Validate sample data JSON
    if (formData.sample_data.trim()) {
      try {
        JSON.parse(formData.sample_data);
      } catch (e) {
        newErrors.sample_data = `Invalid JSON: ${e instanceof Error ? e.message : 'Unknown error'}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Enhanced form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      addNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Please fix the errors in the form before submitting'
      });
      return;
    }

    let requestData: CreateActionRequest | UpdateActionRequest | null = null;

    try {
      setLoading(true);

      const cleanedInputParams = formData.input_params
        .filter(field => field.name.trim())
        .map(cleanFieldConstraints);

      const cleanedOutputParams = formData.output_params
        .filter(field => field.name.trim())
        .map(cleanFieldConstraints);

      requestData = {
        action_name: formData.action_name.trim(),
        description: formData.description.trim() || undefined,
        flow_name: formData.flow_name.trim(),
        input_params: cleanedInputParams,
        output_params: cleanedOutputParams,
        required_fields: formData.required_fields,
        example_use_case: formData.example_use_case.trim() || undefined,
        sample_data: formData.sample_data.trim() ? JSON.parse(formData.sample_data) : undefined
      };

      // Add auto-populate flag for create mode
      if (mode === 'create') {
        (requestData as any).auto_populate_from_flow = formData.auto_populate_from_flow;
      }

      let savedAction: Action;

      if (mode === 'create') {
        savedAction = await ActionsApi.createAction({
          topic_id: topic.id,
          ...requestData
        } as CreateActionRequest);
      } else {
        savedAction = await ActionsApi.updateAction(
          topic.id, 
          action!.id, 
          requestData
        );
      }

      addNotification({
        type: 'success',
        title: mode === 'create' ? 'Action created successfully!' : 'Action updated successfully!',
        message: `Action "${savedAction.action_name}" has been ${mode === 'create' ? 'created' : 'updated'}`
      });

      onSuccess(savedAction);

      if (mode === 'create') {
        setShowCreateAnother(true);
      } else {
        onClose();
      }

    } catch (err) {
      const error = ActionsApi.handleApiError(err);
      
      let errorTitle = `Failed to ${mode} action`;
      let errorMessage = error.message;

      if (error.message.includes('required_fields')) {
        errorTitle = 'Required Fields Error';
        errorMessage = 'Some required fields don\'t match your input parameters. Please check your configuration.';
      } else if (error.message.includes('JSON')) {
        errorTitle = 'Data Format Error';
        errorMessage = 'There was an issue with the data format. Please check your field configurations.';
      } else if (error.message.includes('duplicate') || error.message.includes('unique')) {
        errorTitle = 'Duplicate Error';
        errorMessage = 'An action with this name already exists in this topic.';
      }

      addNotification({
        type: 'error',
        title: errorTitle,
        message: errorMessage,
        duration: 8000
      });

      console.error('Action form submission error:', {
        originalError: err,
        formData,
        requestData
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnother = () => {
    setShowCreateAnother(false);
    setFormData({
      action_name: '',
      description: '',
      flow_name: '',
      input_params: [],
      output_params: [],
      required_fields: [],
      example_use_case: '',
      sample_data: '{}',
      auto_populate_from_flow: true
    });
    setErrors({});
    setFlowValidation({
      status: 'idle',
      exists: false,
      definition: null,
      error: null,
      lastChecked: null
    });
    setAutoParams({
      input_params: [],
      output_params: [],
      required_fields: [],
      sample_data: null
    });
  };

  const handleFinalClose = () => {
    setShowCreateAnother(false);
    onClose();
  };

  const getFlowStatusBadge = () => {
    if (flowValidation.status === 'validating') {
      return (
        <Badge variant="secondary" className="flex items-center gap-1">
          <RefreshCw className="h-3 w-3 animate-spin" />
          Validating...
        </Badge>
      );
    }

    switch (flowValidation.status) {
      case 'valid':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Valid Flow
          </Badge>
        );
      case 'invalid':
        return (
          <Badge variant="destructive" className="flex items-center gap-1">
            <XCircle className="h-3 w-3" />
            Flow Not Found
          </Badge>
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  // Success dialog for "Create Another"
  if (showCreateAnother) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Action Created Successfully!
            </h3>
            <p className="text-gray-600 mb-6">
              Your action has been created. Would you like to create another action for this topic?
            </p>
            <div className="flex gap-3 justify-center">
              <Button type="button" variant="outline" onClick={handleFinalClose}>
                Done
              </Button>
              <Button type="button" onClick={handleCreateAnother}>
                <Plus className="h-4 w-4 mr-2" />
                Create Another
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-8">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {mode === 'create' ? 'Create New Action' : 'Edit Action'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Topic: {topic.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-6 space-y-8">
            {/* Auto-populate Toggle (only for create mode) */}
            {mode === 'create' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wand2 className="h-5 w-5 text-blue-600" />
                    <div>
                      <h3 className="font-medium text-blue-900">Auto-populate from Salesforce Flow</h3>
                      <p className="text-sm text-blue-700">
                        Automatically detect and populate input/output parameters from your flow definition
                      </p>
                    </div>
                  </div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.auto_populate_from_flow}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        auto_populate_from_flow: e.target.checked 
                      }))}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-blue-900">Enable</span>
                  </label>
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Action Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.action_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, action_name: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.action_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Create Support Case"
                  />
                  {errors.action_name && (
                    <p className="text-xs text-red-600 mt-1">{errors.action_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Flow Name <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.flow_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, flow_name: e.target.value }))}
                      className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.flow_name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="e.g., SLAVOAI_CREATE_CASE"
                    />
                    {mode === 'create' && formData.flow_name && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => validateAndPopulateFromFlow(formData.flow_name)}
                        disabled={flowValidation.status === 'validating'}
                      >
                        <RefreshCw className={`h-4 w-4 ${flowValidation.status === 'validating' ? 'animate-spin' : ''}`} />
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {getFlowStatusBadge()}
                  </div>
                  {errors.flow_name && (
                    <p className="text-xs text-red-600 mt-1">{errors.flow_name}</p>
                  )}
                  {flowValidation.error && (
                    <p className="text-xs text-red-600 mt-1">{flowValidation.error}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={2}
                  placeholder="Describe what this action does..."
                />
              </div>
            </div>

            {/* Auto-population Status */}
            {mode === 'create' && formData.auto_populate_from_flow && flowValidation.status === 'valid' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-green-900">Parameters Auto-populated</h3>
                    <p className="text-sm text-green-700 mt-1">
                      Found {autoParams.input_params.length} input parameters and {autoParams.output_params.length} output parameters from flow definition.
                      All fields are set as optional by default.
                    </p>
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <div className="flex items-start gap-2">
                        <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-blue-800">
                          <strong>Next Step:</strong> Review the parameters below and manually check the "Required" box for fields that are mandatory for your use case. 
                          Salesforce flows don't reliably indicate which fields are required, so you need to determine this based on your business logic.
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        {autoParams.input_params.length} inputs
                      </span>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        {autoParams.output_params.length} outputs
                      </span>
                      <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                        0 required (set manually)
                      </span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowParamPreview(!showParamPreview)}
                  >
                    {showParamPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}

            {/* Parameter Preview */}
            {showParamPreview && (formData.input_params.length > 0 || formData.output_params.length > 0) && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <h3 className="font-medium mb-3">Parameter Preview</h3>
                
                {formData.input_params.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Input Parameters ({formData.input_params.length})</h4>
                    <div className="space-y-2">
                      {formData.input_params.map((param, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          <code className="bg-white px-2 py-1 rounded border">{param.name}</code>
                          <Badge variant="outline">{param.type}</Badge>
                          {param.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                          {param.sample_value && (
                            <span className="text-gray-600">Sample: {param.sample_value}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {formData.output_params.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Output Parameters ({formData.output_params.length})</h4>
                    <div className="space-y-2">
                      {formData.output_params.map((param, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          <code className="bg-white px-2 py-1 rounded border">{param.name}</code>
                          <Badge variant="outline">{param.type}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Input Parameters - Always show, but disable editing of field definitions when auto-populated */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Input Parameters</h3>
                {(!formData.auto_populate_from_flow || mode === 'edit') && (
                  <Button type="button" variant="outline" size="sm" onClick={addInputField}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Input
                  </Button>
                )}
              </div>

              {formData.auto_populate_from_flow && mode === 'create' && formData.input_params.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-600" />
                    <p className="text-sm text-blue-800">
                      Parameters below were auto-populated from your Salesforce flow. You can only modify the "Required" status - field names and types are locked to match your flow definition.
                    </p>
                  </div>
                </div>
              )}

              {errors.input_params && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <p className="text-sm text-red-600">{errors.input_params}</p>
                </div>
              )}

              <div className="space-y-3">
                {formData.input_params.map((field, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                      <div className="md:col-span-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Field Name
                        </label>
                        <input
                          type="text"
                          value={field.name}
                          onChange={(e) => updateInputField(index, { name: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          placeholder="field_name"
                          disabled={formData.auto_populate_from_flow && mode === 'create'}
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Type
                        </label>
                        <select
                          value={field.type}
                          onChange={(e) => updateInputField(index, { type: e.target.value as any })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          disabled={formData.auto_populate_from_flow && mode === 'create'}
                        >
                          <option value="string">String</option>
                          <option value="number">Number</option>
                          <option value="boolean">Boolean</option>
                          <option value="picklist">Picklist</option>
                          <option value="multiselect">Multiselect</option>
                        </select>
                      </div>

                      <div className="md:col-span-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Sample Value
                        </label>
                        <input
                          type="text"
                          value={field.sample_value || ''}
                          onChange={(e) => updateInputField(index, { sample_value: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          placeholder="example value"
                          disabled={formData.auto_populate_from_flow && mode === 'create'}
                        />
                      </div>

                      <div className="md:col-span-2 flex items-center justify-center pt-5">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.required_fields.includes(field.name)}
                            onChange={() => toggleRequiredField(field.name)}
                            className="rounded"
                            disabled={!field.name.trim()}
                          />
                          <span className="text-xs text-gray-700">Required</span>
                        </label>
                      </div>

                      <div className="md:col-span-2 flex justify-end pt-5">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeInputField(index)}
                          className="text-red-600 hover:text-red-700"
                          disabled={formData.auto_populate_from_flow && mode === 'create'}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    {(field.type === 'picklist' || field.type === 'multiselect') && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Picklist Values
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Comma-separated values: Option1, Option2, Option3"
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                            value={field.constraints?.values?.join(', ') || ''}
                            onChange={(e) => {
                              const values = e.target.value.split(',').map(v => v.trim()).filter(Boolean);
                              updateInputField(index, {
                                constraints: values.length > 0 ? { values } : undefined
                              });
                            }}
                            disabled={formData.auto_populate_from_flow && mode === 'create'}
                          />
                          <Button type="button" variant="outline" size="sm" className="text-xs">
                            <Download className="h-3 w-3 mr-1" />
                            Fetch from SF
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {formData.input_params.length === 0 && (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-gray-600">No input parameters defined</p>
                  {(!formData.auto_populate_from_flow || mode === 'edit') && (
                    <Button type="button" variant="outline" size="sm" onClick={addInputField} className="mt-2">
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Input
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Output Parameters - Always show, but disable editing of field definitions when auto-populated */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Output Parameters</h3>
                {(!formData.auto_populate_from_flow || mode === 'edit') && (
                  <Button type="button" variant="outline" size="sm" onClick={addOutputField}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Output
                  </Button>
                )}
              </div>

              {formData.auto_populate_from_flow && mode === 'create' && formData.output_params.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-600" />
                    <p className="text-sm text-blue-800">
                      Output parameters below were auto-populated from your Salesforce flow. Field names and types are locked to match your flow definition.
                    </p>
                  </div>
                </div>
              )}

              {errors.output_params && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <p className="text-sm text-red-600">{errors.output_params}</p>
                </div>
              )}

              <div className="space-y-3">
                {formData.output_params.map((field, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                      <div className="md:col-span-5">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Field Name
                        </label>
                        <input
                          type="text"
                          value={field.name}
                          onChange={(e) => updateOutputField(index, { name: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          placeholder="output_field_name"
                          disabled={formData.auto_populate_from_flow && mode === 'create'}
                        />
                      </div>

                      <div className="md:col-span-5">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Type
                        </label>
                        <select
                          value={field.type}
                          onChange={(e) => updateOutputField(index, { type: e.target.value as any })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          disabled={formData.auto_populate_from_flow && mode === 'create'}
                        >
                          <option value="string">String</option>
                          <option value="number">Number</option>
                          <option value="boolean">Boolean</option>
                        </select>
                      </div>

                      <div className="md:col-span-2 flex justify-end items-end">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeOutputField(index)}
                          className="text-red-600 hover:text-red-700"
                          disabled={formData.auto_populate_from_flow && mode === 'create'}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {formData.output_params.length === 0 && (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-gray-600">No output parameters defined</p>
                  {(!formData.auto_populate_from_flow || mode === 'edit') && (
                    <Button type="button" variant="outline" size="sm" onClick={addOutputField} className="mt-2">
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Output
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Additional Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Example Use Case
                </label>
                <textarea
                  value={formData.example_use_case}
                  onChange={(e) => setFormData(prev => ({ ...prev, example_use_case: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={2}
                  placeholder="Describe when and how this action would be used..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sample Data (JSON)
                  <Info className="inline h-4 w-4 ml-1 text-gray-400" />
                </label>
                <textarea
                  value={formData.sample_data}
                  onChange={(e) => setFormData(prev => ({ ...prev, sample_data: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm ${
                    errors.sample_data ? 'border-red-500' : 'border-gray-300'
                  }`}
                  rows={4}
                  placeholder='{\n  "field1": "example value",\n  "field2": 123\n}'
                />
                {errors.sample_data && (
                  <p className="text-xs text-red-600 mt-1">{errors.sample_data}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Example JSON payload for testing this action
                </p>
              </div>
            </div>

            {/* Required Fields Summary */}
            {formData.required_fields.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Required Fields Summary</h4>
                <div className="flex flex-wrap gap-2">
                  {formData.required_fields.map(fieldName => (
                    <Badge key={fieldName} variant="secondary" className="text-blue-700 bg-blue-100">
                      {fieldName}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {errors.required_fields && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <p className="text-sm text-red-600">{errors.required_fields}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading || (mode === 'create' && formData.auto_populate_from_flow && flowValidation.status !== 'valid')}
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {mode === 'create' ? 'Creating...' : 'Updating...'}
                </>
              ) : (
                mode === 'create' ? 'Create Action' : 'Update Action'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActionForm;